import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react'

import { useCallback, useEffect, useRef } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

interface DriftCarouselProps<T> {
    items: Array<T>
    renderItem: (item: T, index: number) => ReactNode
    getKey: (item: T, index: number) => string
    label: string
    speed?: number
    gap?: number
    className?: string
}

/**
 * An endless row that drifts slowly on its own, pauses on hover, and can be
 * dragged or swiped. It bleeds off both edges of the screen.
 */
export const DriftCarousel = <T,>({
    items,
    renderItem,
    getKey,
    label,
    speed = 26,
    gap = 20,
    className
}: DriftCarouselProps<T>) => {
    const viewportRef = useRef<HTMLDivElement>(null)
    const trackRef = useRef<HTMLDivElement>(null)
    const reduced = useReducedMotion()
    const visible = useInView(viewportRef, { once: false, threshold: 0 })
    const visibleRef = useRef(false)
    const motion = useRef({
        offset: 0,
        setWidth: 0,
        velocity: 0,
        nudge: 0,
        dragging: false,
        hovered: false,
        pointerId: -1,
        lastX: 0,
        lastTime: 0,
        travelled: 0
    })

    visibleRef.current = visible

    useEffect(() => {
        const track = trackRef.current

        if (!track) return

        const measure = () => {
            motion.current.setWidth = (track.scrollWidth + gap) / 2
        }

        measure()

        const observer = new ResizeObserver(measure)

        observer.observe(track)

        return () => observer.disconnect()
    }, [items.length, gap])

    useEffect(() => {
        let frame = 0
        let last = performance.now()

        const tick = (now: number) => {
            const state = motion.current
            const delta = Math.min(64, now - last) / 1000

            last = now

            if (!state.dragging) {
                if (Math.abs(state.nudge) > 0.5) {
                    const step = state.nudge * Math.min(1, delta * 7)

                    state.offset += step
                    state.nudge -= step
                }
                else if (Math.abs(state.velocity) > 5) {
                    state.offset += state.velocity * delta
                    state.velocity *= Math.pow(0.03, delta)
                }
                else if (!state.hovered && !reduced && visibleRef.current) {
                    state.offset -= speed * delta
                }
            }

            const width = state.setWidth

            if (width > 0) state.offset = (((state.offset % width) + width) % width) - width

            if (trackRef.current) trackRef.current.style.transform = `translate3d(${state.offset}px, 0, 0)`

            frame = requestAnimationFrame(tick)
        }

        frame = requestAnimationFrame(tick)

        return () => cancelAnimationFrame(frame)
    }, [reduced, speed])

    const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return

        const state = motion.current

        state.dragging = true
        state.pointerId = event.pointerId
        state.lastX = event.clientX
        state.lastTime = performance.now()
        state.velocity = 0
        state.nudge = 0
        state.travelled = 0
    }

    const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
        const state = motion.current

        if (!state.dragging || event.pointerId !== state.pointerId) return

        const dx = event.clientX - state.lastX
        const now = performance.now()
        const elapsed = Math.max(1, now - state.lastTime)

        state.offset += dx
        state.travelled += Math.abs(dx)
        state.velocity = Math.max(-2600, Math.min(2600, (dx / elapsed) * 1000))
        state.lastX = event.clientX
        state.lastTime = now

        if (state.travelled > 6 && !viewportRef.current?.hasPointerCapture(event.pointerId)) {
            viewportRef.current?.setPointerCapture(event.pointerId)
        }
    }

    const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
        const state = motion.current

        if (event.pointerId !== state.pointerId) return

        state.dragging = false

        if (performance.now() - state.lastTime > 80) state.velocity = 0
        if (viewportRef.current?.hasPointerCapture(event.pointerId)) viewportRef.current.releasePointerCapture(event.pointerId)
    }

    const step = useCallback((direction: 1 | -1) => {
        const firstCard = trackRef.current?.firstElementChild as HTMLElement | null
        const distance = (firstCard?.offsetWidth ?? 320) + gap

        motion.current.velocity = 0
        motion.current.nudge += -direction * distance
    }, [gap])

    return (
        <div className={className}>
            <div
                ref={viewportRef}
                role='region'
                aria-roledescription='carousel'
                aria-label={label}
                className='fade-x relative cursor-grab touch-pan-y select-none overflow-hidden active:cursor-grabbing'
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onPointerEnter={() => { motion.current.hovered = true }}
                onPointerLeave={() => { motion.current.hovered = false }}
                onFocusCapture={() => { motion.current.hovered = true }}
                onBlurCapture={() => { motion.current.hovered = false }}
                onClickCapture={event => {
                    if (motion.current.travelled > 6) {
                        event.preventDefault()
                        event.stopPropagation()
                    }
                }}
                onDragStart={event => event.preventDefault()}
            >
                <div ref={trackRef} className='flex w-max will-change-transform' style={{ gap }}>
                    {items.map((item, index) => (
                        <div key={getKey(item, index)} className='shrink-0'>
                            {renderItem(item, index)}
                        </div>
                    ))}

                    {items.map((item, index) => (
                        <div key={`copy-${getKey(item, index)}`} className='shrink-0' aria-hidden='true' inert>
                            {renderItem(item, index)}
                        </div>
                    ))}
                </div>
            </div>

            <div className='shell mt-8 flex items-center justify-end gap-2'>
                <button
                    type='button'
                    onClick={() => step(-1)}
                    aria-label='Previous'
                    className={cn('glass grid size-11 place-items-center rounded-full text-cream transition-transform duration-300 hover:-translate-x-0.5')}
                >
                    <ArrowLeft className='size-4' />
                </button>

                <button
                    type='button'
                    onClick={() => step(1)}
                    aria-label='Next'
                    className='glass grid size-11 place-items-center rounded-full text-cream transition-transform duration-300 hover:translate-x-0.5'
                >
                    <ArrowRight className='size-4' />
                </button>
            </div>
        </div>
    )
}
